<template>
  <div
    class="chat-editor"
    :class="{ 'is-focused': isFocused, 'is-disabled': disabled, 'is-compact': compact, 'is-drag-over': isDragOver }"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div class="toolbar" v-if="!disabled && showToolbar">
      <button type="button" class="tb-btn" :class="{ active: is('bold') }" @mousedown.prevent @click="exec('toggleBold')" title="Bold — Ctrl+B"><b>B</b></button>
      <button type="button" class="tb-btn" :class="{ active: is('italic') }" @mousedown.prevent @click="exec('toggleItalic')" title="Italic — Ctrl+I"><i>I</i></button>
      <button type="button" class="tb-btn" :class="{ active: is('underline') }" @mousedown.prevent @click="exec('toggleUnderline')" title="Underline — Ctrl+U"><u>U</u></button>
      <button type="button" class="tb-btn" :class="{ active: is('strike') }" @mousedown.prevent @click="exec('toggleStrike')" title="Strikethrough — Ctrl+Shift+X"><s>S</s></button>
      <span class="tb-sep"></span>
      <button type="button" class="tb-btn" :class="{ active: is('bulletList') }" @mousedown.prevent @click="exec('toggleBulletList')" title="Bulleted list — Ctrl+Shift+8">&bull;</button>
      <button type="button" class="tb-btn" :class="{ active: is('orderedList') }" @mousedown.prevent @click="exec('toggleOrderedList')" title="Numbered list — Ctrl+Shift+7">1.</button>
      <button type="button" class="tb-btn" :class="{ active: is('blockquote') }" @mousedown.prevent @click="exec('toggleBlockquote')" title="Quote — Ctrl+Shift+9">&ldquo;</button>
      <span class="tb-sep"></span>
      <button type="button" class="tb-btn" :class="{ active: is('heading', { level: 1 }) }" @mousedown.prevent @click="toggleHeading(1)" title="Heading 1 — Ctrl+Alt+1">H1</button>
      <button type="button" class="tb-btn" :class="{ active: is('heading', { level: 2 }) }" @mousedown.prevent @click="toggleHeading(2)" title="Heading 2 — Ctrl+Alt+2">H2</button>
      <button type="button" class="tb-btn" :class="{ active: is('heading', { level: 3 }) }" @mousedown.prevent @click="toggleHeading(3)" title="Heading 3 — Ctrl+Alt+3">H3</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="exec('setHorizontalRule')" title="Horizontal rule">&mdash;</button>
      <span class="tb-sep"></span>
      <button type="button" class="tb-btn" :class="{ active: is('code') }" @mousedown.prevent @click="exec('toggleCode')" title="Inline code — Ctrl+E"><code>&lt;/&gt;</code></button>
      <button type="button" class="tb-btn" :class="{ active: is('codeBlock') }" @mousedown.prevent @click="exec('toggleCodeBlock')" title="Code block — Ctrl+Alt+C"><code>{ }</code></button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="insertTable" title="Insert table">⊞</button>
      <button type="button" class="tb-btn" :class="{ active: is('link') }" @mousedown.prevent @click="setLink" title="Link — Ctrl+K">&#128279;</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="clearFormatting" title="Clear formatting — Ctrl+\">⌫</button>
      <span class="tb-sep"></span>
      <button v-if="allowMentions" type="button" class="tb-btn" @mousedown.prevent @click="insertMentionTrigger" title="Mention someone (@)">@</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="insertSlashTrigger" title="Slash command (/)">/</button>
      <button v-if="allowAttachments" type="button" class="tb-btn" @mousedown.prevent @click="triggerFilePick" title="Attach file">📎</button>
      <button type="button" class="tb-btn shortcut-toggle" :class="{ active: showShortcuts }" @mousedown.prevent @click="showShortcuts = !showShortcuts" title="Keyboard shortcuts">?</button>
      <input ref="fileInput" type="file" multiple style="display:none" :accept="acceptAttribute" @change="onFileInputChange" />
    </div>

    <div v-if="showTableToolsBar" class="image-size-bar table-tools-bar">
      <span class="tb-label">Table:</span>
      <button type="button" class="tb-btn" @mousedown.prevent @click="tableAddRowAbove" title="Add row above">+Row ↑</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="tableAddRowBelow" title="Add row below">+Row ↓</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="tableAddColLeft" title="Add column left">+Col ←</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="tableAddColRight" title="Add column right">+Col →</button>
      <span class="tb-sep"></span>
      <button type="button" class="tb-btn" @mousedown.prevent @click="tableDeleteRow" title="Delete row">−Row</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="tableDeleteCol" title="Delete column">−Col</button>
      <span class="tb-sep"></span>
      <button type="button" class="tb-btn" @mousedown.prevent @click="tableToggleHeaderRow" title="Toggle header row">Header</button>
      <button type="button" class="tb-btn tb-btn-danger" @mousedown.prevent @click="tableDelete" title="Delete entire table">× Delete table</button>
    </div>

    <div v-if="showMediaSizeBar" class="image-size-bar">
      <span class="tb-label">{{ videoSelected ? 'Video size:' : 'Image size:' }}</span>
      <label class="img-size-field">W
        <input type="text" v-model="imgWidth" @input="updateMediaSize" @focus="sizeBarFocused = true" @blur="sizeBarFocused = false" placeholder="auto" />
      </label>
      <label class="img-size-field">H
        <input type="text" v-model="imgHeight" @input="updateMediaSize" @focus="sizeBarFocused = true" @blur="sizeBarFocused = false" placeholder="auto" />
      </label>
      <span class="tb-sep"></span>
      <button type="button" class="tb-btn" @mousedown.prevent @click="applyMediaSize('25%', '')" title="25% wide">25%</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="applyMediaSize('50%', '')" title="50% wide">50%</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="applyMediaSize('75%', '')" title="75% wide">75%</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="applyMediaSize('100%', '')" title="Full width">100%</button>
      <button type="button" class="tb-btn" @mousedown.prevent @click="resetMediaSize" title="Reset to original">Auto</button>
      <span class="hint">Accepts <code>200px</code>, <code>50%</code>, <code>3em</code></span>
    </div>

    <div class="shortcut-panel" v-if="showShortcutPanel">
      <div class="shortcut-grid">
        <div><kbd>Ctrl</kbd>+<kbd>B</kbd> Bold</div>
        <div><kbd>Ctrl</kbd>+<kbd>I</kbd> Italic</div>
        <div><kbd>Ctrl</kbd>+<kbd>U</kbd> Underline</div>
        <div><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>X</kbd> Strike</div>
        <div><kbd>Ctrl</kbd>+<kbd>E</kbd> Inline code</div>
        <div><kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>C</kbd> Code block</div>
        <div><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>8</kbd> Bullet list</div>
        <div><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>7</kbd> Numbered list</div>
        <div><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>9</kbd> Quote</div>
        <div><kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>1..3</kbd> Heading</div>
        <div><kbd>#</kbd>/<kbd>##</kbd>/<kbd>###</kbd> + space Heading (md)</div>
        <div><kbd>---</kbd> + enter Horizontal rule</div>
        <div><kbd>Ctrl</kbd>+<kbd>K</kbd> Link</div>
        <div><kbd>Ctrl</kbd>+<kbd>\</kbd> Clear format</div>
        <div><kbd>@</kbd> Mention</div>
        <div><kbd>/</kbd> Slash command</div>
        <div v-if="submitOnEnter"><kbd>Enter</kbd> Send</div>
        <div v-if="submitOnEnter"><kbd>Shift</kbd>+<kbd>Enter</kbd> New line</div>
        <div v-if="!submitOnEnter"><kbd>Enter</kbd> New paragraph (new block)</div>
        <div v-if="!submitOnEnter"><kbd>Shift</kbd>+<kbd>Enter</kbd> New line (same block)</div>
        <div><kbd>Ctrl</kbd>+<kbd>Enter</kbd> {{ submitOnEnter ? 'Force send' : 'Save (' + sendLabel + ')' }}</div>
      </div>
    </div>

    <editor-content :editor="editor" class="editor-surface rich-content" />

    <div class="actions" v-if="showActions">
      <span class="hint" v-if="submitOnEnter">
        <strong>Enter</strong> to send &middot; <strong>Shift+Enter</strong> for new line &middot; <strong>@</strong> mention &middot; <strong>/</strong> commands
      </span>
      <span class="hint" v-else>
        <strong>Enter</strong> new paragraph &middot; <strong>Shift+Enter</strong> new line (same block) &middot; <strong>Ctrl+Enter</strong> to {{ sendLabel.toLowerCase() }} &middot; click the button to save
      </span>
      <button type="button" class="send-btn" :disabled="isSendDisabled" @click="send">
        {{ sendLabel }}
      </button>
    </div>

    <!-- Link-URL prompt. We hold the modal open in @ok (preventDefault) so
         applyLinkFromDialog can mutate the doc and close it deterministically;
         Cancel discards the value. -->
    <b-modal
      v-model="linkDialogOpen"
      title="Insert link"
      ok-title="Apply"
      cancel-title="Cancel"
      ok-variant="primary"
      centered
      hide-header-close
      modal-class="forum-editor-link-modal"
      @ok="applyLinkFromDialog"
      @cancel="cancelLinkDialog"
      @hidden="cancelLinkDialog"
    >
      <label class="link-modal-label" for="forum-editor-link-input">URL</label>
      <input
        id="forum-editor-link-input"
        ref="linkInput"
        type="url"
        v-model="linkDialogValue"
        placeholder="https://example.com"
        class="link-modal-input"
        @keydown.enter.prevent="applyLinkFromDialog"
      />
      <div class="link-modal-hint">
        Leave the URL empty (or the default <code>https://</code>) and press Apply to remove the existing link.
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Editor, EditorContent, VueRenderer, VueNodeViewRenderer } from '@tiptap/vue-2'
import { Extension, Node, mergeAttributes } from '@tiptap/core'
import AttachmentNodeView from './AttachmentNodeView.vue'
import { uploadFile } from '../../../utils/attachment-uploader'
import { iconForMime, formatBytes, colorForMime, readableFileType } from '../../../utils/attachment-utility'
// Single source of truth for the sanitization allowlist lives in
// utils/markdown.js — both the editor paste path and the read-side
// markdownToHTML render share it so the two can never drift apart.

// Detects when "pasted HTML" is really just plain text that the OS/browser
// wrapped in minimal <html>/<p>/<div> tags (Notepad, VS Code, terminal paste).
// In that case we want to return '' from transformPastedHTML so Tiptap falls
// back to text/plain — where tiptap-markdown's transformPastedText parses
// markdown syntax into structured nodes.
// If the HTML has any *meaningful* formatting tag, we keep the HTML path
// (DOMPurify-sanitized) and let the existing pipeline handle it.
const FORMATTING_TAG_RE = /<(strong|em|b|i|u|s|del|ins|h[1-6]|ul|ol|li|a|img|table|thead|tbody|tr|th|td|blockquote|code|pre|video|audio|hr|details|summary|sub|sup|mark|abbr|kbd|cite|q|dl|dt|dd)\b/i

// Expose a global map for the NodeView's cancel button to reach the XHR
if (typeof window !== 'undefined' && !window.__attachmentUploadControllers) {
  window.__attachmentUploadControllers = new Map()
}
import StarterKit from '@tiptap/starter-kit'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import Image from '@tiptap/extension-image'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import { Suggestion } from '@tiptap/suggestion'
import tippy from 'tippy.js'

import MentionList from './MentionList.vue'
import { toMarkdown, markdownToHTML, purifyHTML } from '../../../utils/markdown'
import { ATTACHMENT_REGISTRY } from '../../../utils/attachment-registry'

const MentionWithLabel = Mention.extend({
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: el => el.getAttribute('data-id'),
        renderHTML: attrs => (attrs.id ? { 'data-id': attrs.id } : {})
      },
      label: {
        default: null,
        parseHTML: el => el.getAttribute('data-label'),
        renderHTML: attrs => (attrs.label ? { 'data-label': attrs.label } : {})
      },
      kind: {
        default: 'user',
        parseHTML: el => el.getAttribute('data-kind') || 'user',
        renderHTML: attrs => (attrs.kind ? { 'data-kind': attrs.kind } : {})
      }
    }
  }
})

// ATTACHMENT_REGISTRY (imported above from utils/attachment-registry) is the
// single source of truth for both the markdown parser's [[kind:id]] resolver
// and the upload tracker that surfaces in-flight progress / temp IDs.

const Attachment = Node.create({
  name: 'attachment',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      id: { default: null },
      src: { default: null },
      name: { default: '' },
      mime: { default: '' },
      size: { default: 0 },
      kind: { default: 'file' },
      uploading: { default: false },
      progress: { default: 0 },
      error: { default: null },
      width: { default: null },   // video sizing — markdown token: |w=...
      height: { default: null }   // video sizing — markdown token: |h=...
    }
  },
  parseHTML() {
    // Parser only matches the HTML our markdown-it token rule emits.
    return [
      { tag: 'video[src][data-id]', getAttrs: el => ({
        id: el.getAttribute('data-id'),
        src: el.getAttribute('src'),
        kind: 'video',
        name: el.getAttribute('title') || '',
        mime: el.getAttribute('data-mime') || 'video/*',
        size: Number(el.getAttribute('data-size') || 0),
        width: el.getAttribute('data-width') || null,
        height: el.getAttribute('data-height') || null
      }) },
      { tag: 'audio[src][data-id]', getAttrs: el => ({ id: el.getAttribute('data-id'), src: el.getAttribute('src'), kind: 'audio', name: el.getAttribute('title') || '', mime: el.getAttribute('data-mime') || 'audio/*', size: Number(el.getAttribute('data-size') || 0) }) },
      // File attachments come from markdown.js expanding [[file:id]] tokens
      // into <a class="md-attachment md-attachment-link" data-type="attachment" data-kind="file" ...>.
      // Without this entry the editor would parse the anchor as a plain
      // markdown link on edit-load, losing the [[file:id]] round-trip.
      { tag: 'a[data-type="attachment"]', getAttrs: el => ({ id: el.getAttribute('data-id'), src: el.getAttribute('data-src') || el.getAttribute('href'), kind: el.getAttribute('data-kind') || 'file', name: el.getAttribute('data-name') || '', mime: el.getAttribute('data-mime') || '', size: Number(el.getAttribute('data-size') || 0) }) },
      { tag: 'div[data-type="attachment"]', getAttrs: el => ({ id: el.getAttribute('data-id'), src: el.getAttribute('data-src'), kind: el.getAttribute('data-kind') || 'file', name: el.getAttribute('data-name') || '', mime: el.getAttribute('data-mime') || '', size: Number(el.getAttribute('data-size') || 0) }) }
    ]
  },
  renderHTML({ node }) {
    // renderHTML is used by getHTML() — for rendering OUTSIDE the editor
    // (MessageBubble, email, PDF). Vue NodeView handles in-editor display.
    const { id, src, name, mime, size, kind, width, height } = node.attrs
    if (kind === 'video') {
      const attrs = { src, controls: 'true', title: name, 'data-mime': mime, 'data-size': size, 'data-id': id, class: 'md-media md-media-video' }
      if (width)  attrs['data-width'] = width
      if (height) attrs['data-height'] = height
      if (width || height) {
        const styleParts = []
        if (width)  styleParts.push(`width:${width}`)
        if (height) styleParts.push(`height:${height}`)
        styleParts.push('max-width:100%')
        attrs.style = styleParts.join(';')
      }
      return ['video', attrs]
    }
    if (kind === 'audio') {
      return ['audio', { src, controls: 'true', title: name, 'data-mime': mime, 'data-size': size, 'data-id': id, class: 'md-media md-media-audio' }]
    }
    // PDF previews open in a new tab; everything else triggers a download.
    // Whole card is the link; native <a> attributes drive behaviour
    // (window.open() is unreliable on Safari/Mac).
    const isInline = mime === 'application/pdf'
    const linkAttrs = isInline
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : { download: name, rel: 'noopener noreferrer' }
    const actionIcon = isInline ? 'fa-external-link-alt' : 'fa-download'
    const tile = colorForMime(mime)
    return ['a', {
      href: src || '#',
      ...linkAttrs,
      'data-type': 'attachment',
      'data-id': id, 'data-src': src, 'data-kind': 'file',
      'data-name': name, 'data-mime': mime, 'data-size': size,
      class: 'md-attachment md-attachment-link',
      title: (isInline ? 'Open ' : 'Download ') + (name || 'file')
    },
      ['span', { class: 'md-attachment-left', style: `background:${tile.bg}` },
        ['i', { class: 'fas ' + iconForMime(mime), style: `color:${tile.color}` }]
      ],
      ['span', { class: 'md-attachment-meta' },
        ['span', { class: 'md-attachment-name' }, name || 'file'],
        ['span', { class: 'md-attachment-sub' }, formatBytes(size) + ' · ' + readableFileType(mime, name)]
      ],
      ['span', { class: 'md-attachment-action', 'aria-hidden': 'true' },
        ['i', { class: 'fas ' + actionIcon }]
      ]
    ]
  },
  addNodeView() {
    return VueNodeViewRenderer(AttachmentNodeView)
  }
})

const SizedImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: el => el.getAttribute('style') || null,
        renderHTML: attrs => attrs.style ? { style: attrs.style } : {}
      },
      width: {
        default: null,
        parseHTML: imgElement => {
          const widthAttr = imgElement.getAttribute('width') || imgElement.style.width
          return widthAttr || null
        },
        renderHTML: () => ({})
      },
      height: {
        default: null,
        parseHTML: imgElement => {
          const heightAttr = imgElement.getAttribute('height') || imgElement.style.height
          return heightAttr || null
        },
        renderHTML: () => ({})
      }
    }
  }
})

const SLASH_COMMANDS = [
  { id: 'code', name: 'Code block', type: 'Fenced code block', icon: '{ }', run: editor => editor.chain().focus().toggleCodeBlock().run() },
  { id: 'quote', name: 'Quote', type: 'Blockquote', icon: '❝', run: editor => editor.chain().focus().toggleBlockquote().run() },
  { id: 'bullet', name: 'Bulleted list', type: 'Unordered list', icon: '•', run: editor => editor.chain().focus().toggleBulletList().run() },
  { id: 'number', name: 'Numbered list', type: 'Ordered list', icon: '1.', run: editor => editor.chain().focus().toggleOrderedList().run() },
  { id: 'table', name: 'Table', type: 'Insert 3x3 table with header row', icon: '⊞', run: editor => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  { id: 'bold', name: 'Bold', type: 'Toggle bold', icon: 'B', run: editor => editor.chain().focus().toggleBold().run() },
  { id: 'italic', name: 'Italic', type: 'Toggle italic', icon: 'I', run: editor => editor.chain().focus().toggleItalic().run() },
  { id: 'strike', name: 'Strikethrough', type: 'Toggle strike', icon: 'S', run: editor => editor.chain().focus().toggleStrike().run() },
  { id: 'clear', name: 'Clear formatting', type: 'Reset formatting', icon: '×', run: editor => editor.chain().focus().clearNodes().unsetAllMarks().run() }
]

export default {
  name: 'MarkdownEditor',
  components: { EditorContent },
  props: {
    value: { type: [Object, String], default: null },
    placeholder: { type: String, default: 'Type a new message. @ to mention, / for commands, drop files to attach.' },
    disabled: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    showToolbar: { type: Boolean, default: true },
    showActions: { type: Boolean, default: true },
    sendLabel: { type: String, default: 'Send' },
    searchUsers: { type: Function, default: null },
    submitOnEnter: { type: Boolean, default: false },
    // Polymorphic entity type ("thread", "comment") — sent as the Entity
    // field on the upload request and used for the upload-time policy
    // lookup. Required.
    uploadEntity: { type: String, required: true },
    // When false, hides the @ toolbar button and disables the @ suggestion
    // dropdown. Existing mention nodes in loaded content still render.
    allowMentions: { type: Boolean, default: true },
    // When false, hides the attach toolbar button and short-circuits
    // paste/drop. Existing attachments in loaded content still render.
    allowAttachments: { type: Boolean, default: true },
    // Allowlist of MIME patterns. Empty array = nothing allowed.
    allowedAttachmentTypes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      editor: null,
      isFocused: false,
      isEmpty: true,
      showShortcuts: false,
      isDragOver: false,
      dragCounter: 0,
      imageSelected: false,
      videoSelected: false,
      inTable: false,
      imgWidth: '',
      imgHeight: '',
      sizeBarFocused: false,
      uploadingCount: 0,
      // Drives the link-URL b-modal. Native dialogs are not used.
      linkDialogOpen: false,
      linkDialogValue: '',
      // ProseMirror selection captured when the link modal opens — opening
      // a BV modal blurs the editor, which on some browsers (mobile Safari
      // especially) collapses the selection to a caret. We restore from
      // this snapshot before applying the link mark.
      linkDialogSavedRange: null
    }
  },
  computed: {
    isSendDisabled() {
      if (this.disabled) return true
      if (this.uploadingCount > 0) return true
      if (this.isEmpty) return true
      return false
    },
    // For the file picker dialog: comma-separated MIME glob list. Browser
    // honors this to filter the OS file chooser, but we still re-validate
    // mime types in JS because users can paste/drop bypassing the picker.
    acceptAttribute() {
      return (this.allowedAttachmentTypes || []).join(',')
    },
    // Shared gate for every secondary toolbar (table tools, media size,
    // shortcut panel). When the editor is disabled or the host opted out
    // of the toolbar, none of these context bars should appear.
    isToolbarActive() {
      return !this.disabled && this.showToolbar
    },
    hasMediaFocus() {
      return this.imageSelected || this.videoSelected || this.sizeBarFocused
    },
    showMediaSizeBar() {
      return this.hasMediaFocus && this.isToolbarActive
    },
    showTableToolsBar() {
      return this.inTable && this.isToolbarActive
    },
    showShortcutPanel() {
      return this.showShortcuts && this.isToolbarActive
    }
  },
  watch: {
    value(v) {
      if (this.editor && v !== undefined && this.editor.getJSON() !== v) {
        this.editor.commands.setContent(v || '')
        this.isEmpty = this.editor.isEmpty
      }
    },
    // When the link-prompt modal opens, push focus into the URL input
    // (and select its contents) so the user can immediately type/replace.
    linkDialogOpen(isOpen) {
      if (!isOpen) return
      this.$nextTick(() => {
        const input = this.$refs.linkInput
        if (!input) return
        input.focus()
        if (typeof input.select === 'function') input.select()
      })
    }
  },
  mounted() {
    const self = this

    const SubmitOnEnter = Extension.create({
      name: 'submitOnEnter',
      priority: 1000,
      addKeyboardShortcuts() {
        const trySend = () => { self.send(); return true }
        return {
          Enter: () => {
            if (!self.submitOnEnter) return false
            if (this.editor.isActive('codeBlock')) return false
            if (this.editor.isActive('bulletList') || this.editor.isActive('orderedList')) return false
            return trySend()
          },
          'Mod-Enter': trySend,
          'Shift-Enter': () => this.editor.commands.setHardBreak()
        }
      }
    })

    const TeamsShortcuts = Extension.create({
      name: 'teamsShortcuts',
      addKeyboardShortcuts() {
        return {
          'Mod-k': () => { self.setLink(); return true },
          'Mod-\\': () => this.editor.chain().focus().clearNodes().unsetAllMarks().run(),
          'Mod-Shift-9': () => this.editor.chain().focus().toggleBlockquote().run(),
          'Mod-Shift-k': () => this.editor.chain().focus().toggleCodeBlock().run(),
          'Mod-Shift-c': () => this.editor.chain().focus().toggleCode().run()
        }
      }
    })

    const SlashCommands = Extension.create({
      name: 'slashCommands',
      addProseMirrorPlugins() {
        return [Suggestion({
          editor: this.editor,
          char: '/',
          startOfLine: false,
          allowSpaces: false,
          command: ({ editor, range, props }) => {
            editor.chain().focus().deleteRange(range).run()
            props.run(editor)
          },
          items: ({ query }) => {
            const queryText = query.toLowerCase()
            return SLASH_COMMANDS.filter(slashCommand =>
              slashCommand.id.includes(queryText) || slashCommand.name.toLowerCase().includes(queryText)
            )
          },
          render: () => {
            let component
            let popup
            const hasItems = (props) => Array.isArray(props && props.items) && props.items.length > 0
            return {
              onStart(props) {
                const slashItems = props.items.map(rawItem => ({ id: rawItem.id, name: rawItem.name, type: rawItem.type, color: '#464775' }))
                component = new VueRenderer(MentionList, {
                  parent: self,
                  propsData: { ...props, items: slashItems, command: (selectedItem) => {
                    const fullCommand = SLASH_COMMANDS.find(slashCommand => slashCommand.id === selectedItem.id)
                    if (fullCommand) props.command(fullCommand)
                  }}
                })
                if (!props.clientRect) return
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: hasItems(props),
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                  arrow: false,
                  offset: [0, 6],
                  // Custom theme so the unscoped tippy reset only matches
                  // OUR popups via [data-theme~='forum-mention'].
                  theme: 'forum-mention'
                })
              },
              onUpdate(props) {
                const slashItems = props.items.map(rawItem => ({ id: rawItem.id, name: rawItem.name, type: rawItem.type, color: '#464775' }))
                component.updateProps({ ...props, items: slashItems, command: (selectedItem) => {
                  const fullCommand = SLASH_COMMANDS.find(slashCommand => slashCommand.id === selectedItem.id)
                  if (fullCommand) props.command(fullCommand)
                }})
                if (!popup) return
                if (props.clientRect) popup[0].setProps({ getReferenceClientRect: props.clientRect })
                if (hasItems(props)) popup[0].show()
                else popup[0].hide()
              },
              onKeyDown(props) {
                if (props.event.key === 'Escape') { popup && popup[0].hide(); return true }
                return (component.ref && component.ref.onKeyDown(props)) || false
              },
              onExit() {
                if (popup) popup[0].destroy()
                if (component) component.destroy()
              }
            }
          }
        })]
      }
    })

    // Build extension list — Mention is conditional on `allowMentions` prop.
    const extensions = [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      Underline,
      Placeholder.configure({ placeholder: this.placeholder }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        // XSS defense: reject javascript: / vbscript: / data:text URLs that could execute script.
        // Only allow http(s), mailto, tel, relative, hash links.
        validate: (href) => /^(https?:\/\/|mailto:|tel:|\/|\.\/|#|blob:)/i.test(href),
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' }
      }),
      SizedImage.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: 'md-inline-image' } }),
      Attachment,
      Table.configure({ resizable: false, HTMLAttributes: { class: 'md-table' } }),
      TableRow, TableHeader, TableCell,
      TaskList.configure({ HTMLAttributes: { class: 'md-task-list' } }),
      TaskItem.configure({ nested: true, HTMLAttributes: { class: 'md-task-item' } }),
      SlashCommands,
      TeamsShortcuts,
      SubmitOnEnter
    ]
    if (this.allowMentions) {
      // Mention node is in the schema only when allowed — disables both the
      // @ suggestion and the ability to parse incoming mention nodes from
      // pasted markdown. Existing mention spans loaded via parseHTML still
      // render their text content.
      extensions.splice(extensions.length - 3, 0,
        MentionWithLabel.configure({
          HTMLAttributes: { class: 'mention' },
          suggestion: this.buildMentionSuggestion()
        })
      )
    }

    this.editor = new Editor({
      content: this.value || '',
      autofocus: this.autofocus,
      editable: !this.disabled,
      extensions,
      editorProps: {
        // Two-tier paste strategy:
        // 1) If the clipboard HTML is just wrapped plain text (common when copying
        //    markdown from Notepad/VS Code/terminal — browsers auto-wrap in <html>),
        //    return '' so Tiptap uses text/plain → tiptap-markdown parses it as markdown.
        // 2) If it's real HTML (bold, lists, links, etc. from a browser/Word),
        //    DOMPurify-sanitize and pass through normally.
        transformPastedHTML: (html) => {
          if (this.looksLikePlainTextWrapper(html)) return ''
          return purifyHTML(html)
        },
        handleDOMEvents: {
          paste: (view, event) => {
            // 1) File paste → attachment flow
            const files = Array.from(event.clipboardData?.files || [])
            if (files.length) {
              event.preventDefault()
              files.forEach(file => this.insertFileAsMarkdown(file))
              return true
            }
            // 2) Plain markdown text paste → render via markdown-it + custom rules
            const html = (event.clipboardData && event.clipboardData.getData('text/html')) || ''
            const text = (event.clipboardData && event.clipboardData.getData('text/plain')) || ''
            if (text && !FORMATTING_TAG_RE.test(html)) {
              event.preventDefault()
              const rendered = markdownToHTML(text)
              this.editor.chain().focus().insertContent(rendered).run()
              return true
            }
            // 3) Otherwise let Tiptap's default HTML paste path run
            //    (transformPastedHTML above will DOMPurify it).
            return false
          }
        }
      },
      onFocus: () => { this.isFocused = true },
      onBlur: () => { this.isFocused = false },
      onUpdate: () => {
        this.isEmpty = this.editor.isEmpty
        this.imageSelected = this.editor.isActive('image')
        this.videoSelected = this.editor.isActive('attachment', { kind: 'video' })
        this.inTable = this.editor.isActive('table')
        this.uploadingCount = this.countPendingUploads()
        this.$emit('update', this.serialize())
        this.$emit('typing')
      },
      onTransaction: () => {
        this.uploadingCount = this.countPendingUploads()
      },
      onSelectionUpdate: () => {
        this.imageSelected = this.editor.isActive('image')
        this.videoSelected = this.editor.isActive('attachment', { kind: 'video' })
        this.inTable = this.editor.isActive('table')
        this.syncMediaSizeFromSelection()
      },
      onCreate: () => { this.isEmpty = this.editor.isEmpty }
    })
  },
  beforeDestroy() {
    if (this.editor) this.editor.destroy()
  },
  methods: {
    // Detects clipboard HTML that's just plain text wrapped in <html>/
    // <body>/<p> by Notepad/VS Code/terminal copies. When true, the
    // paste handler returns '' so Tiptap falls back to text/plain and
    // tiptap-markdown parses the markdown syntax instead of treating
    // it as opaque HTML.
    looksLikePlainTextWrapper(html) {
      if (!html) return true
      return !FORMATTING_TAG_RE.test(html)
    },
    // Generates a temp client-side id for an in-progress attachment
    // upload. Replaced by the backend id once the upload completes; in
    // the meantime the id keeps the placeholder node addressable for
    // progress updates and cancel.
    newAttachmentId() {
      return 'att-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
    },
    // Coerce a width/height input into a CSS-friendly value: bare digits
    // become px ("200" → "200px"); units the user typed ("50%", "3em")
    // pass through; empty/whitespace returns null so the caller knows
    // to skip the dimension entirely.
    normalizeDim(rawDimension) {
      if (rawDimension == null || rawDimension === '') return null
      const trimmedDimension = String(rawDimension).trim()
      if (!trimmedDimension) return null
      if (/^\d+$/.test(trimmedDimension)) return trimmedDimension + 'px'
      return trimmedDimension
    },
    buildMentionSuggestion() {
      const self = this
      return {
        char: '@',
        items: ({ query }) => {
          if (typeof self.searchUsers === 'function') {
            return Promise.resolve(self.searchUsers(query)).then(matches => (matches || []).slice(0, 8))
          }
          const queryText = query.toLowerCase()
          return self.users
            .filter(candidateUser => {
              const lowerName = (candidateUser.name || '').toLowerCase()
              const lowerType = (candidateUser.type || '').toLowerCase()
              return lowerName.includes(queryText) || lowerType.includes(queryText)
            })
            .slice(0, 8)
        },
        render: () => {
          let component
          let popup
          const hasItems = (props) => Array.isArray(props && props.items) && props.items.length > 0
          return {
            onStart(props) {
              component = new VueRenderer(MentionList, { parent: self, propsData: props })
              if (!props.clientRect) return
              popup = tippy('body', {
                getReferenceClientRect: props.clientRect,
                appendTo: () => document.body,
                content: component.element,
                // No-results state stays hidden — popup only appears once
                // there's at least one match. onUpdate flips visibility as
                // the query narrows/widens.
                showOnCreate: hasItems(props),
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                arrow: false,
                offset: [0, 6],
                theme: 'forum-mention'
              })
            },
            onUpdate(props) {
              component.updateProps(props)
              if (!popup) return
              if (props.clientRect) popup[0].setProps({ getReferenceClientRect: props.clientRect })
              if (hasItems(props)) popup[0].show()
              else popup[0].hide()
            },
            onKeyDown(props) {
              if (props.event.key === 'Escape') { popup && popup[0].hide(); return true }
              return (component.ref && component.ref.onKeyDown(props)) || false
            },
            onExit() {
              if (popup) popup[0].destroy()
              if (component) component.destroy()
            }
          }
        }
      }
    },
    is(mark, attrs) { return this.editor ? this.editor.isActive(mark, attrs) : false },
    exec(cmd, ...args) {
      if (!this.editor) return
      this.editor.chain().focus()[cmd](...args).run()
    },
    toggleHeading(level) {
      if (!this.editor) return
      this.editor.chain().focus().toggleHeading({ level }).run()
    },
    // Opens the link-URL b-modal (see template). The actual mark mutation
    // happens in applyLinkFromDialog.
    setLink() {
      if (!this.editor) return
      const prev = this.editor.getAttributes('link').href
      // Snapshot the selection BEFORE opening the modal — opening a BV
      // modal moves focus into the dialog which can collapse ProseMirror's
      // selection. We restore from {from, to} in applyLinkFromDialog so
      // the link mark applies to the originally-highlighted range.
      const sel = this.editor.state.selection
      this.linkDialogSavedRange = sel ? { from: sel.from, to: sel.to } : null
      this.linkDialogValue = prev || 'https://'
      this.linkDialogOpen = true
    },
    applyLinkFromDialog(bvModalEvt) {
      if (!this.editor) return
      // Hold the modal open while we mutate the doc, then close ourselves.
      if (bvModalEvt && typeof bvModalEvt.preventDefault === 'function') {
        bvModalEvt.preventDefault()
      }
      const url = (this.linkDialogValue || '').trim()
      let chain = this.editor.chain().focus()
      // Restore the selection captured at modal-open time so the mark
      // mutation targets the original range, not whatever the editor's
      // current caret happens to be after the focus round-trip.
      const range = this.linkDialogSavedRange
      if (range && Number.isFinite(range.from) && Number.isFinite(range.to)) {
        chain = chain.setTextSelection({ from: range.from, to: range.to })
      }
      chain = chain.extendMarkRange('link')
      if (url === '' || url === 'https://') {
        chain.unsetLink().run()
      } else {
        chain.setLink({ href: url }).run()
      }
      this.linkDialogOpen = false
      this.linkDialogValue = ''
      this.linkDialogSavedRange = null
    },
    cancelLinkDialog() {
      this.linkDialogOpen = false
      this.linkDialogValue = ''
      this.linkDialogSavedRange = null
    },
    clearFormatting() {
      if (!this.editor) return
      this.editor.chain().focus().clearNodes().unsetAllMarks().run()
    },
    insertTable() {
      if (!this.editor) return
      this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
    },
    tableAddRowAbove() { this.editor && this.editor.chain().focus().addRowBefore().run() },
    tableAddRowBelow() { this.editor && this.editor.chain().focus().addRowAfter().run() },
    tableAddColLeft()  { this.editor && this.editor.chain().focus().addColumnBefore().run() },
    tableAddColRight() { this.editor && this.editor.chain().focus().addColumnAfter().run() },
    tableDeleteRow()   { this.editor && this.editor.chain().focus().deleteRow().run() },
    tableDeleteCol()   { this.editor && this.editor.chain().focus().deleteColumn().run() },
    tableToggleHeaderRow() { this.editor && this.editor.chain().focus().toggleHeaderRow().run() },
    async tableDelete() {
      if (!this.editor) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Delete this entire table? This cannot be undone.',
        {
          title: 'Delete table?',
          okTitle: 'Delete',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
        },
      )
      if (confirmed) this.editor.chain().focus().deleteTable().run()
    },
    insertMentionTrigger() {
      if (!this.editor) return
      this.editor.chain().focus().insertContent('@').run()
    },
    insertSlashTrigger() {
      if (!this.editor) return
      this.editor.chain().focus().insertContent('/').run()
    },
    triggerFilePick() {
      this.$refs.fileInput && this.$refs.fileInput.click()
    },
    onFileInputChange(changeEvent) {
      const files = Array.from(changeEvent.target.files || [])
      files.forEach(file => this.insertFileAsMarkdown(file))
      changeEvent.target.value = ''
    },
    onDragOver() {
      this.dragCounter++
      this.isDragOver = true
    },
    onDragLeave() {
      this.dragCounter = Math.max(0, this.dragCounter - 1)
      if (this.dragCounter === 0) this.isDragOver = false
    },
    onDrop(dropEvent) {
      this.dragCounter = 0
      this.isDragOver = false
      const files = Array.from(dropEvent.dataTransfer?.files || [])
      files.forEach(file => this.insertFileAsMarkdown(file))
    },
    isAttachmentTypeAllowed(file) {
      const patterns = this.allowedAttachmentTypes || []
      if (!patterns.length) return false
      const lowerMime = String(file.type || '').toLowerCase()
      const fileName = String(file.name || '')
      const dotIdx = fileName.lastIndexOf('.')
      const fileExt = dotIdx > 0 && dotIdx < fileName.length - 1
        ? fileName.slice(dotIdx + 1).toLowerCase()
        : ''
      return patterns.some(rawPattern => {
        const pattern = String(rawPattern).trim().toLowerCase()
        if (!pattern) return false
        // MIME pattern (contains '/').
        if (pattern.includes('/')) {
          if (!lowerMime) return false
          if (pattern.endsWith('/*')) return lowerMime.startsWith(pattern.slice(0, -1))
          return pattern === lowerMime
        }
        // Extension pattern (".pdf" or "pdf").
        if (!fileExt) return false
        const ext = pattern.startsWith('.') ? pattern.slice(1) : pattern
        return ext === fileExt
      })
    },
    insertFileAsMarkdown(file) {
      if (!this.editor || !file) return
      // Backend gates uploads against the same SystemParameter, but if
      // attachments are off for this entity we don't even open the
      // upload dialog — the user gets immediate feedback instead of an
      // HTTP rejection.
      if (!this.allowAttachments) {
        this.$emit('attachment-rejected', { file, name: file.name || 'file', mime: file.type || '', reason: 'attachments-disabled' })
        return
      }
      const name = file.name || 'file'
      const mime = file.type || ''
      const size = file.size || 0

      if (!this.isAttachmentTypeAllowed(file)) {
        this.$emit('attachment-rejected', { file, name, mime, reason: 'type-not-allowed' })
        if (this.$toasted) {
          this.$toasted.error(
            `File type "${mime || name || 'unknown'}" is not allowed. Allowed: ${this.allowedAttachmentTypes.join(', ')}`,
            { icon: 'exclamation-triangle', theme: 'outline', position: 'bottom-center', singleton: true, duration: 3000 },
          )
        }
        return
      }

      const blobUrl = URL.createObjectURL(file)

      if (mime.startsWith('image/')) {
        // Insert with blob URL immediately for preview; upload swaps to CDN URL on completion.
        this.editor.chain().focus().setImage({ src: blobUrl, alt: name, title: name }).run()
        const upload = uploadFile(file, () => { /* image progress not shown */ }, { entity: this.uploadEntity })
        upload.then(result => {
          // Find the image by blob URL and replace src with the CDN URL
          const tr = this.editor.state.tr
          let replaced = false
          this.editor.state.doc.descendants((node, pos) => {
            if (replaced) return false
            if (node.type.name === 'image' && node.attrs.src === blobUrl) {
              tr.setNodeMarkup(pos, null, { ...node.attrs, src: result.fileURL })
              replaced = true
              return false
            }
          })
          if (replaced) this.editor.view.dispatch(tr)
          this.$emit('attachment-registered', { ...result, kind: 'image' })
        }).catch(err => {
          if (!err.cancelled) console.error('Image upload failed:', err)
        })
        return
      }

      // Video/audio/file flow: insert node in uploading state, start upload, update on progress/complete.
      const kind = mime.startsWith('video/') ? 'video'
        : mime.startsWith('audio/') ? 'audio'
        : 'file'
      const tempId = this.newAttachmentId()

      this.editor.chain().focus().insertContent({
        type: 'attachment',
        attrs: { id: tempId, kind, name, mime, size, src: blobUrl, uploading: true, progress: 0 }
      }).run()

      // Register provisional record in the canonical (backend-camelCase)
      // shape, plus editor-only `uploading`/`progress` flags. The keys
      // match AttachmentViewModel exactly so the markdown-it renderer
      // doesn't have to know about an in-progress vs. completed shape.
      ATTACHMENT_REGISTRY.set(String(tempId), {
        id:            tempId,
        fileURL:       blobUrl,
        fileName:      name,
        mimeType:      mime,
        fileSizeBytes: size,
        uploading:     true,
        progress:      0,
      })

      const upload = uploadFile(file, (progress) => {
        this.updateAttachmentAttrs(tempId, { progress })
        const existing = ATTACHMENT_REGISTRY.get(String(tempId))
        if (existing) ATTACHMENT_REGISTRY.set(String(tempId), { ...existing, progress })
      }, { entity: this.uploadEntity })

      // Expose cancel so the NodeView's × button can abort
      window.__attachmentUploadControllers.set(tempId, upload.cancel)

      upload.then(result => {
        window.__attachmentUploadControllers.delete(tempId)
        // Swap the temp id for the server-issued id.
        const finalRecord = {
          id:            result.id,
          fileURL:       result.fileURL,
          fileName:      result.fileName,
          mimeType:      result.mimeType,
          fileSizeBytes: result.fileSizeBytes,
          createdAt:     result.createdAt,
        }
        ATTACHMENT_REGISTRY.delete(String(tempId))
        ATTACHMENT_REGISTRY.set(String(result.id), finalRecord)
        // Update the Tiptap node: swap id + clear uploading flag. The
        // node's internal attrs use {src/name/mime/size} (Tiptap schema);
        // we feed it the camelCase fields from `result`.
        this.updateAttachmentAttrs(tempId, {
          id: result.id, src: result.fileURL,
          name: result.fileName, mime: result.mimeType, size: result.fileSizeBytes,
          uploading: false, progress: 100, error: null,
        })
        // Free blob URL memory
        try { URL.revokeObjectURL(blobUrl) } catch (revokeError) {}
        this.$emit('attachment-registered', finalRecord)
      }).catch(err => {
        window.__attachmentUploadControllers.delete(tempId)
        if (err && err.cancelled) {
          // Node was already deleted by cancel button; nothing to do
          ATTACHMENT_REGISTRY.delete(String(tempId))
          try { URL.revokeObjectURL(blobUrl) } catch (revokeError) {}
        } else {
          this.updateAttachmentAttrs(tempId, { uploading: false, error: err.message || 'upload failed' })
          const existing = ATTACHMENT_REGISTRY.get(String(tempId))
          if (existing) ATTACHMENT_REGISTRY.set(String(tempId), { ...existing, uploading: false, error: err.message })
        }
      })
    },
    updateAttachmentAttrs(id, patch) {
      if (!this.editor) return
      const tr = this.editor.state.tr
      let found = false
      this.editor.state.doc.descendants((node, pos) => {
        if (found) return false
        if (node.type.name === 'attachment' && node.attrs.id === id) {
          tr.setNodeMarkup(pos, null, { ...node.attrs, ...patch })
          found = true
          return false
        }
      })
      if (found) this.editor.view.dispatch(tr)
    },
    registerAttachments(attachments) {
      if (!Array.isArray(attachments)) return
      for (const attachment of attachments) {
        if (attachment && attachment.id != null) ATTACHMENT_REGISTRY.set(String(attachment.id), attachment)
      }
    },
    countPendingUploads() {
      if (!this.editor) return 0
      let pendingCount = 0
      this.editor.state.doc.descendants(node => {
        if (node.type.name === 'attachment' && node.attrs.uploading) pendingCount++
      })
      return pendingCount
    },
    syncMediaSizeFromSelection() {
      if (!this.editor) return
      if (this.imageSelected) {
        const attrs = this.editor.getAttributes('image') || {}
        const style = attrs.style || ''
        const wMatch = style.match(/(?:^|;)\s*width\s*:\s*([^;]+)/i)
        const hMatch = style.match(/(?:^|;)\s*height\s*:\s*([^;]+)/i)
        this.imgWidth = wMatch ? wMatch[1].trim() : (attrs.width || '')
        this.imgHeight = hMatch ? hMatch[1].trim() : (attrs.height || '')
      } else if (this.videoSelected) {
        const attrs = this.editor.getAttributes('attachment') || {}
        this.imgWidth = attrs.width || ''
        this.imgHeight = attrs.height || ''
      } else {
        this.imgWidth = ''
        this.imgHeight = ''
      }
    },
    updateMediaSize() {
      if (!this.editor) return
      if (!this.imageSelected && !this.videoSelected && !this.sizeBarFocused) return
      const normalizedWidth  = this.normalizeDim(this.imgWidth)
      const normalizedHeight = this.normalizeDim(this.imgHeight)
      if (this.imageSelected) {
        // Image uses inline-style serialization (markdown has no width syntax)
        const styleParts = []
        if (normalizedWidth)  styleParts.push('width:'  + normalizedWidth)
        if (normalizedHeight) styleParts.push('height:' + normalizedHeight)
        if (styleParts.length) styleParts.push('max-width:100%')
        const style = styleParts.length ? styleParts.join(';') : null
        this.editor.chain().updateAttributes('image', { style, width: null, height: null }).run()
      } else if (this.videoSelected) {
        // Video stores width/height as separate attrs — markdown token: [[video:id|w=...|h=...]]
        this.editor.chain().updateAttributes('attachment', { width: normalizedWidth, height: normalizedHeight }).run()
      }
    },
    applyMediaSize(w, h) {
      this.imgWidth = w || ''
      this.imgHeight = h || ''
      this.updateMediaSize()
    },
    resetMediaSize() {
      this.imgWidth = ''
      this.imgHeight = ''
      if (!this.editor) return
      if (this.imageSelected) {
        this.editor.chain().updateAttributes('image', { style: null, width: null, height: null }).run()
      } else if (this.videoSelected) {
        this.editor.chain().updateAttributes('attachment', { width: null, height: null }).run()
      }
    },
    serialize() {
      const json = this.editor.getJSON()
      const html = this.editor.getHTML()
      const text = this.editor.getText()
      const markdown = toMarkdown(this.editor)
      const mentions = []
      const referencedIds = new Set()
      this.editor.state.doc.descendants(node => {
        if (node.type.name === 'mention') {
          mentions.push({ id: node.attrs.id, label: node.attrs.label, kind: node.attrs.kind || 'user' })
        }
        if (node.type.name === 'attachment' && node.attrs.id) {
          referencedIds.add(node.attrs.id)
        }
      })
      const attachments = Array.from(referencedIds)
        .map(id => ATTACHMENT_REGISTRY.get(String(id)))
        .filter(Boolean)
      return { json, html, markdown, text, mentions, attachments }
    },
    send() {
      if (this.isSendDisabled) return
      const payload = this.serialize()
      // payload.attachments already comes from serialize() — walks the doc and returns
      // only attachment nodes that are registered. Filter out any still-uploading entries.
      payload.attachments = (payload.attachments || []).filter(attachment => !attachment.uploading)
      this.$emit('send', payload)
      this.editor.commands.clearContent()
      this.isEmpty = true
    },
    focus() { if (this.editor) this.editor.commands.focus() },
    clear() {
      if (this.editor) this.editor.commands.clearContent()
      this.isEmpty = true
    },
    setMarkdown(markdown, attachments) {
      if (!this.editor) return
      if (attachments) this.registerAttachments(attachments)
      this.editor.commands.setContent(markdownToHTML(markdown || ''))
      this.isEmpty = this.editor.isEmpty
    },
    // Insert a real ProseMirror mention node — same shape the @ suggestion
    // dropdown produces. Used by the bubble-up-reply path in
    // ThreadDetailView so the seeded mention renders as the styled pill,
    // not as the raw @[Name](user:id) markdown string.
    insertMention(user) {
      if (!this.editor || !user || user.id == null) return
      const label = String(user.label || user.name || '').trim() || 'user'
      this.editor.chain().focus()
        .insertContent({ type: 'mention', attrs: { id: user.id, label, kind: user.kind || 'user' } })
        .insertContent(' ')
        .run()
      this.isEmpty = this.editor.isEmpty
    }
  }
}
</script>

<style scoped>
/* Editor surface lives entirely inside .chat-editor, which is in this
   component's template — Vue's scoped data-v-* attribute applies. We
   cross into ::v-deep only at the boundary with Tiptap's ProseMirror
   tree and the AttachmentNodeView child component, since those nodes
   don't carry our data-v-*. Colors come from the project's --secondary-*
   palette where it makes sense; the rest are inlined literals so the
   editor's look is self-contained without a separate variable layer. */

.chat-editor {
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  background: #ffffff;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.chat-editor.is-focused {
  border-color: var(--secondary-color, #03324d);
  box-shadow: 0 0 0 2px rgba(3, 50, 77, 0.15);
}
.chat-editor.is-disabled { background: #f5f5f5; opacity: 0.7; }
.chat-editor.is-drag-over {
  border-color: var(--secondary-color, #03324d);
  box-shadow: 0 0 0 3px rgba(3, 50, 77, 0.2);
  background: rgba(3, 50, 77, 0.05);
}

/* Toolbar (template element). Wraps onto multiple rows on narrow
   viewports so every button stays reachable without horizontal scroll. */
.chat-editor .toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid #e1e1e1;
  border-radius: 8px 8px 0 0;
  background: linear-gradient(to bottom, #fcfcfd, #f5f5f8);
  flex-wrap: wrap;
  row-gap: 4px;
}

.chat-editor .tb-btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 5px;
  padding: 0 7px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #616161;
  min-width: 28px;
  height: 28px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
  user-select: none;
  flex-shrink: 0;
  font-family: inherit;
}
.chat-editor .tb-btn code {
  font-family: Consolas, Menlo, Monaco, monospace;
  font-size: 11.5px;
  background: transparent;
  padding: 0;
  letter-spacing: -0.3px;
  /* Inherit the button's text color — otherwise Bootstrap's global
     `<code>` rule (#d63384, pink) leaks in and the inline-code / code
     -block icons read as red while the rest of the toolbar is grey. */
  color: inherit;
}
.chat-editor .tb-btn:hover { background: rgba(3, 50, 77, 0.05); color: #242424; border-color: rgba(0, 0, 0, 0.06); }
.chat-editor .tb-btn:active { background: rgba(3, 50, 77, 0.12); transform: translateY(0.5px); }
/* Active state mirrors the reference editor's look: a light tint of the
   primary-action color (navy) with the dark variant for the icon, plus
   a subtle inset shadow that reads as "pressed in" without being loud. */
.chat-editor .tb-btn.active {
  background: rgba(3, 50, 77, 0.12);
  color: var(--secondary-color, #03324d);
  border-color: rgba(3, 50, 77, 0.35);
  box-shadow: inset 0 0 0 1px rgba(3, 50, 77, 0.08);
}
.chat-editor .tb-btn.active:hover {
  background: rgba(3, 50, 77, 0.2);
}
.chat-editor .tb-btn:focus-visible {
  outline: none;
  border-color: var(--secondary-color, #03324d);
  box-shadow: 0 0 0 2px rgba(3, 50, 77, 0.2);
}
.chat-editor .tb-btn-danger { color: var(--color-danger, #d3131a); }
.chat-editor .tb-btn-danger:hover {
  background: rgba(211, 19, 26, 0.08);
  color: var(--color-danger, #d3131a);
  border-color: rgba(211, 19, 26, 0.25);
}

.chat-editor .tb-sep {
  width: 1px;
  align-self: stretch;
  background: #e1e1e1;
  margin: 4px 4px;
  flex-shrink: 0;
}

.chat-editor .shortcut-toggle { font-weight: 600; }
.chat-editor .shortcut-panel {
  padding: 10px 12px;
  border-bottom: 1px solid #e1e1e1;
  background: #fafbff;
}
.chat-editor .shortcut-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 6px 14px;
  font-size: 12px;
  color: #616161;
}
.chat-editor .shortcut-grid kbd {
  background: #ffffff;
  border: 1px solid #e1e1e1;
  border-bottom-width: 2px;
  border-radius: 4px;
  padding: 1px 5px;
  font-family: Consolas, Menlo, Monaco, monospace;
  font-size: 11px;
  color: #242424;
  margin: 0 1px;
}

/* Editor surface (template element) */
.chat-editor .editor-surface {
  padding: 10px 12px;
  min-height: 60px;
  max-height: 320px;
  overflow-y: auto;
}
.chat-editor.is-compact .editor-surface { min-height: 40px; }

/* ProseMirror tree is rendered by Tiptap, NOT our template — cross the
   scoping boundary with ::v-deep. */
.chat-editor .editor-surface ::v-deep .ProseMirror { outline: none; min-height: 40px; }
.chat-editor .editor-surface ::v-deep .ProseMirror > *:first-child { margin-top: 0; }
.chat-editor .editor-surface ::v-deep .ProseMirror > *:last-child { margin-bottom: 0; }
.chat-editor .editor-surface ::v-deep .ProseMirror p { margin: 4px 0; }
.chat-editor .editor-surface ::v-deep .ProseMirror h1,
.chat-editor .editor-surface ::v-deep .ProseMirror h2,
.chat-editor .editor-surface ::v-deep .ProseMirror h3 { font-weight: 600; margin: 8px 0 4px; line-height: 1.3; }
.chat-editor .editor-surface ::v-deep .ProseMirror h1 { font-size: 1.5em; }
.chat-editor .editor-surface ::v-deep .ProseMirror h2 { font-size: 1.25em; }
.chat-editor .editor-surface ::v-deep .ProseMirror h3 { font-size: 1.1em; }
.chat-editor .editor-surface ::v-deep .ProseMirror h4 { font-size: 1em; font-weight: 600; margin: 6px 0 2px; }
.chat-editor .editor-surface ::v-deep .ProseMirror h5 { font-size: 0.95em; font-weight: 600; margin: 6px 0 2px; color: #616161; }
.chat-editor .editor-surface ::v-deep .ProseMirror h6 { font-size: 0.9em; font-weight: 600; margin: 6px 0 2px; color: #616161; text-transform: uppercase; letter-spacing: 0.5px; }
.chat-editor .editor-surface ::v-deep .ProseMirror hr { border: none; border-top: 1px solid #e1e1e1; margin: 8px 0; }
.chat-editor .editor-surface ::v-deep .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder); float: left; color: #8a8a8a;
  pointer-events: none; height: 0;
}
.chat-editor .editor-surface ::v-deep .ProseMirror ul,
.chat-editor .editor-surface ::v-deep .ProseMirror ol { padding-left: 22px; margin: 4px 0; }
.chat-editor .editor-surface ::v-deep .ProseMirror blockquote {
  border-left: 3px solid var(--secondary-color, #03324d);
  padding: 2px 10px; color: #616161;
  margin: 4px 0; background: rgba(3, 50, 77, 0.06);
  border-radius: 0 4px 4px 0;
}
.chat-editor .editor-surface ::v-deep .ProseMirror code {
  background: rgba(0, 0, 0, 0.06); padding: 1px 5px; border-radius: 4px;
  font-family: Consolas, Menlo, Monaco, monospace; font-size: 0.92em;
}
.chat-editor .editor-surface ::v-deep .ProseMirror pre {
  background: #1f1f1f; color: #e1e1e1;
  padding: 10px; border-radius: 6px;
  font-family: Consolas, Menlo, Monaco, monospace;
  font-size: 12px; margin: 6px 0; overflow-x: auto;
}
.chat-editor .editor-surface ::v-deep .ProseMirror pre code { background: transparent; padding: 0; color: inherit; }
.chat-editor .editor-surface ::v-deep .ProseMirror a { color: var(--secondary-color, #03324d); text-decoration: underline; }
.chat-editor .editor-surface ::v-deep .ProseMirror img.md-inline-image {
  max-width: 100%; max-height: 320px; border-radius: 6px;
  display: block; margin: 6px 0;
}
.chat-editor .editor-surface ::v-deep .ProseMirror video,
.chat-editor .editor-surface ::v-deep .ProseMirror audio { display: block; margin: 6px 0; max-width: 100%; }
.chat-editor .editor-surface ::v-deep .ProseMirror .md-media { display: block; margin: 8px 0; border-radius: 6px; }
.chat-editor .editor-surface ::v-deep .ProseMirror .md-media.md-media-video { max-width: 100%; max-height: 360px; background: #000; }

/* Tables (Tiptap-rendered) */
.chat-editor .editor-surface ::v-deep .ProseMirror table.md-table {
  border-collapse: collapse; margin: 8px 0; table-layout: fixed; width: 100%;
}
.chat-editor .editor-surface ::v-deep .ProseMirror table.md-table td,
.chat-editor .editor-surface ::v-deep .ProseMirror table.md-table th {
  border: 1px solid #e1e1e1; padding: 6px 10px; vertical-align: top;
}
.chat-editor .editor-surface ::v-deep .ProseMirror table.md-table th {
  background: rgba(3, 50, 77, 0.05); font-weight: 600; text-align: left;
}

/* Task lists (Tiptap-rendered) */
.chat-editor .editor-surface ::v-deep .ProseMirror ul.md-task-list { list-style: none; padding-left: 4px; }
.chat-editor .editor-surface ::v-deep .ProseMirror ul.md-task-list li { display: flex; align-items: flex-start; gap: 8px; }
.chat-editor .editor-surface ::v-deep .ProseMirror ul.md-task-list li > label { flex-shrink: 0; margin-top: 3px; }
.chat-editor .editor-surface ::v-deep .ProseMirror ul.md-task-list li > div { flex: 1; }
.chat-editor .editor-surface ::v-deep .ProseMirror ul.md-task-list li[data-checked="true"] > div { text-decoration: line-through; color: #616161; }

/* Mention pill rendered inline by Tiptap */
.chat-editor .editor-surface ::v-deep .ProseMirror .mention {
  background: rgba(3, 50, 77, 0.1);
  color: var(--secondary-color, #03324d);
  padding: 1px 6px; border-radius: 4px;
  font-weight: 500; white-space: nowrap; cursor: default;
}

/* AttachmentNodeView base layout (`.md-attachment`, `.md-attachment-left`,
   `.md-attachment-meta`, `.md-attachment-name`, `.md-attachment-sub`,
   `.md-attachment-action`) is shared with rendered-body cards and the
   static thread / comment lists — defined once in the global
   `rich-content.css` under `.rich-content`. The editor opts in by
   carrying `rich-content` on its editor-content element. Editor-only
   states (selection ring, uploading / error / progress / remove buttons,
   media-wrap) stay scoped below. */
.chat-editor .editor-surface ::v-deep .ProseMirror .md-attachment {
  user-select: none;
}

/* Image / video size bar + table tools bar (template) */
.chat-editor .tb-label { font-size: 11px; color: #616161; padding: 0 4px; text-transform: uppercase; letter-spacing: 0.3px; }
.chat-editor .image-size-bar {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 10px; border-bottom: 1px solid #e1e1e1;
  background: rgba(3, 50, 77, 0.06); flex-wrap: wrap;
}
.chat-editor .image-size-bar.table-tools-bar { background: #fff6e5; }
.chat-editor .img-size-field { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; color: #616161; font-weight: 600; }
.chat-editor .img-size-field input {
  width: 64px; padding: 3px 6px;
  border: 1px solid #e1e1e1; border-radius: 3px;
  font-size: 12px; font-family: Consolas, Menlo, Monaco, monospace;
  outline: none; background: #ffffff;
}
.chat-editor .img-size-field input:focus { border-color: var(--secondary-color, #03324d); box-shadow: 0 0 0 2px rgba(3, 50, 77, 0.15); }
.chat-editor .image-size-bar .hint code { background: rgba(0, 0, 0, 0.05); padding: 1px 4px; border-radius: 3px; font-family: Consolas, Menlo, Monaco, monospace; font-size: 10px; }

/* Attachment NodeView wrappers (inside ProseMirror) */
.chat-editor ::v-deep .md-attachment-node { position: relative; margin: 6px 0; }
.chat-editor ::v-deep .md-attachment-node.is-selected .md-attachment,
.chat-editor ::v-deep .md-attachment-node.is-selected .md-media-wrap { box-shadow: 0 0 0 2px var(--secondary-color, #03324d); }
.chat-editor ::v-deep .md-attachment-uploading { border-left-color: #f4c842; background: #fffbea; }
.chat-editor ::v-deep .md-attachment-error { border-left-color: var(--color-danger, #d3131a); background: #fff5f5; }
.chat-editor ::v-deep .md-attachment-progress-wrap { height: 6px; background: #eee; border-radius: 3px; overflow: hidden; margin: 4px 0; }
.chat-editor ::v-deep .md-attachment-progress-bar { height: 100%; background: var(--secondary-color, #03324d); transition: width 0.2s ease; border-radius: 3px; }
.chat-editor ::v-deep .md-attachment-uploading .md-attachment-sub { display: flex; gap: 8px; align-items: center; font-size: 11px; }
.chat-editor ::v-deep .uploading-label { color: #b08900; font-weight: 600; }
.chat-editor ::v-deep .uploading-percent { color: var(--secondary-color, #03324d); font-weight: 600; }
.chat-editor ::v-deep .uploading-size { color: #616161; }
.chat-editor ::v-deep .md-attachment-btn {
  background: none; border: 1px solid transparent; cursor: pointer;
  font-size: 14px; padding: 4px 8px; border-radius: 4px;
  color: #616161; text-decoration: none; line-height: 1; flex-shrink: 0;
}
.chat-editor ::v-deep .md-attachment-btn:hover { background: rgba(0, 0, 0, 0.05); color: #242424; }
.chat-editor ::v-deep .md-attachment-cancel:hover,
.chat-editor ::v-deep .md-attachment-remove:hover {
  background: rgba(211, 19, 26, 0.1);
  color: var(--color-danger, #d3131a);
  border-color: rgba(211, 19, 26, 0.3);
}

.chat-editor ::v-deep .md-media-wrap { position: relative; display: inline-block; margin: 6px 0; border-radius: 6px; max-width: 100%; }
.chat-editor ::v-deep .md-media-wrap video { display: block; max-width: 100%; max-height: 360px; border-radius: 6px; background: #000; }
.chat-editor ::v-deep .md-media-remove {
  position: absolute; top: 6px; right: 6px;
  background: rgba(0, 0, 0, 0.6); color: white;
  font-size: 16px; padding: 2px 8px; border-radius: 50%;
  opacity: 0; transition: opacity 0.15s; z-index: 2;
}
.chat-editor ::v-deep .md-media-wrap:hover .md-media-remove,
.chat-editor ::v-deep .md-attachment-node.is-selected .md-media-remove { opacity: 1; }
.chat-editor ::v-deep .md-media-remove:hover { background: var(--color-danger, #d3131a); color: white; }
.chat-editor ::v-deep .md-media-wrap-audio { display: flex; align-items: center; gap: 12px; padding: 10px 12px; background: #ffffff; border: 1px solid #e1e1e1; border-radius: 6px; max-width: 500px; }
.chat-editor ::v-deep .md-audio-meta { flex: 1; min-width: 0; }
.chat-editor ::v-deep .md-audio-meta audio { width: 100%; margin-top: 4px; }

/* Send / actions row (template) */
.chat-editor .actions {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 10px; border-top: 1px solid #e1e1e1;
  background: #fafafa;
  border-radius: 0 0 8px 8px;
}
.chat-editor.is-compact .actions { padding: 4px 8px; }
.chat-editor .hint { font-size: 11px; color: #616161; }
.chat-editor .send-btn {
  background: var(--secondary-color, #03324d); color: white; border: none;
  padding: 6px 16px; border-radius: 4px; cursor: pointer; font-weight: 600;
  transition: background 0.15s; font-family: inherit;
}
.chat-editor .send-btn:hover:not(:disabled) { background: var(--secondary-color-hover, #032a41); }
.chat-editor .send-btn:disabled { background: #c8c8c8; cursor: not-allowed; }

/* Mobile: tighten paddings. Toolbar wraps onto multiple rows
   (flex-wrap: wrap on the base rule) so every button stays reachable
   without horizontal scrolling. Tap targets bump to 32px to match the
   WCAG 2.5.5 recommendation now that we have vertical room. */
@media (max-width: 600px) {
  .chat-editor .toolbar {
    padding: 4px 6px;
    gap: 1px;
  }
  .chat-editor .tb-btn { min-width: 32px; height: 32px; }
  .chat-editor .editor-surface {
    padding: 8px 10px;
    /* On phones the modal/composer host expands vertically; let the
       editor grow with content but keep an upper bound so the toolbar
       stays in view when typing long messages. */
    min-height: 120px;
    max-height: 50vh;
  }
  .chat-editor .actions { padding: 4px 8px; flex-wrap: wrap; gap: 6px; }
  .chat-editor .hint { display: none; }
  .chat-editor ::v-deep .ProseMirror .md-attachment { max-width: 100%; }
  /* iOS auto-zooms <16px inputs on focus — bump the prosemirror text
     to 16px so the modal doesn't jump on tap. */
  .chat-editor ::v-deep .ProseMirror { font-size: 16px; }
}
</style>

<!-- Tippy wraps our MentionList in a body-mounted element that no Vue
     scope hash can reach. We tagged each tippy instance with
     theme: 'forum-mention', so the [data-theme~='forum-mention']
     attribute selector is highly specific and only resets OUR popups —
     no risk of bleeding onto other tippy usage in the app. -->
<style>
.tippy-box[data-theme~='forum-mention'] {
  background: transparent;
  box-shadow: none;
}
.tippy-box[data-theme~='forum-mention'] > .tippy-content {
  padding: 0;
}

/* Link-prompt modal — BV mounts modals on <body>, so scoped styles can't
   reach the dialog. Every selector below is gated on the modal-class we
   pass (.forum-editor-link-modal) so this can't leak into other modals. */
.forum-editor-link-modal .link-modal-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #606060;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.forum-editor-link-modal .link-modal-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ececec;
  border-radius: 8px;
  /* 16px keeps iOS Safari from auto-zooming when the input gains focus
     on phones — same trick the new-thread modal uses on its title. */
  font-size: 16px;
  color: #0f0f0f;
  background: #fafafa;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}
.forum-editor-link-modal .link-modal-input:focus {
  background: #ffffff;
  border-color: var(--secondary-color, #03324d);
  box-shadow: 0 0 0 3px rgba(3, 50, 77, 0.1);
}
.forum-editor-link-modal .link-modal-hint {
  font-size: 12px;
  color: #909090;
  margin-top: 8px;
  line-height: 1.5;
}
.forum-editor-link-modal .link-modal-hint code {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 5px;
  border-radius: 4px;
  font-family: Consolas, Menlo, Monaco, monospace;
  font-size: 0.9em;
}
</style>
